import { MedplumClient } from '@medplum/core';
import { useMount } from 'react-use';
import { redirectToAuth } from 'supertokens-auth-react';
import Session from 'supertokens-auth-react/recipe/session';

import { Alert, AlertTitle, LoadingIcon, Stack } from '@eluve/components';
import { clearSkippedInterstitials } from '@eluve/interstitials';
import { usePostHog } from '@eluve/posthog-react';

/**
 * Revoke the Medplum session tied to this browser. Safe no-op when the
 * tenant doesn't use Medplum or the user never logged in to it — we just
 * construct a client against the same base URL so it rehydrates the
 * persisted `activeLogin` from localStorage, then sign it out to clear
 * tokens on the server and drop the localStorage entries the SDK writes.
 */
const signOutFromMedplum = async (baseUrl: string | undefined) => {
  if (!baseUrl) return;
  try {
    const client = new MedplumClient({ baseUrl });
    await client.signOut();
  } catch {
    // ignore — nothing to sign out of, or network error
  }
};

export interface LogoutPageProps {
  medplumBaseUrl?: string;
}

export const LogoutPage: React.FC<LogoutPageProps> = ({ medplumBaseUrl }) => {
  const posthog = usePostHog();

  const handleLogout = async () => {
    clearSkippedInterstitials();
    posthog?.reset();
    await signOutFromMedplum(medplumBaseUrl);
    await Session.signOut();
    await redirectToAuth({
      redirectBack: false,
    });
  };

  useMount(handleLogout);

  return (
    <Stack className="min-h-screen p-4" gap={2} align="center" justify="center">
      <Alert className="flex max-w-md items-center gap-2" variant="default">
        <div>
          <LoadingIcon size="xl" />
        </div>
        <AlertTitle>Signing out...</AlertTitle>
      </Alert>
    </Stack>
  );
};
